import type { ReactElement, ReactNode } from "react";
import { cn } from "~/lib/utils";
import { badgeVariants } from "../../ui/badge";
import { HoverCard, HoverCardPopup, HoverCardTrigger } from "../../ui/hover-card";

export const REVIEW_REFERENCE_CLASS_NAME =
  "group/source pointer-events-auto relative z-10 flex min-h-8 w-full max-w-full min-w-0 cursor-pointer items-center gap-2 rounded-[var(--control-radius)] py-1 text-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring min-[901px]:w-auto";
export const REVIEW_REFERENCE_TEXT_CLASS_NAME =
  "min-w-0 flex-1 min-[901px]:flex min-[901px]:items-baseline min-[901px]:gap-2";

/**
 * A reference behind a finding, shown as a badge beside its status and
 * severity rather than as a row of its own: the card's text is the finding,
 * and where it came from is metadata. The hover card carries the detail, and
 * `reveal` widens the badge on hover, focus or while the card is open, so the
 * destination is named before anyone follows it.
 */
export function ReviewReferenceBadge({
  render,
  icon,
  label,
  reveal,
  accessibleLabel,
  className,
  children,
}: {
  readonly render: ReactElement;
  readonly icon?: ReactNode;
  readonly label: string;
  readonly reveal?: ReactNode;
  readonly accessibleLabel: string;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  return (
    <HoverCard>
      <HoverCardTrigger
        render={render}
        aria-label={accessibleLabel}
        data-review-reference
        className={cn(
          badgeVariants({ variant: "secondary" }),
          "group/source pointer-events-auto relative z-10 h-auto max-w-full min-w-0 cursor-pointer gap-0 border-0 px-2 py-1 text-xs sm:h-auto",
          className,
        )}
      >
        <span className="inline-flex min-w-0 items-center gap-1.5">
          {icon}
          <span className="min-w-0 truncate">{label}</span>
        </span>
        {reveal ? (
          <span
            aria-hidden
            className="grid grid-cols-[0fr] transition-[grid-template-columns] duration-200 ease-out group-hover/source:grid-cols-[1fr] group-focus-visible/source:grid-cols-[1fr] group-data-[popup-open]/source:grid-cols-[1fr] motion-reduce:transition-none"
          >
            <span className="min-w-0 overflow-hidden whitespace-nowrap">
              <span className="ml-1.5 inline-flex items-center gap-1 border-l border-current/20 pl-1.5">
                {reveal}
              </span>
            </span>
          </span>
        ) : null}
      </HoverCardTrigger>
      <HoverCardPopup side="top" align="start" className="w-80 max-w-[calc(100vw-2rem)] p-4">
        {children}
      </HoverCardPopup>
    </HoverCard>
  );
}
